import { useForm } from "@inertiajs/react"

interface GpsDeviceForm {
  id?: number
  name: string
  serial_number: string
  start_date: string | null
  end_date: string | null
  log: string
  sort_order: number | null
  notes: string
  current_user_id: number | null
}

interface Investigator {
  id: number
  name: string
}

interface Props {
  gps_device: GpsDeviceForm
  investigators: Investigator[]
  errors?: Record<string, string[]>
  submitUrl: string
  submitMethod: "post" | "patch"
}

export default function GpsDeviceFormComponent({
  gps_device,
  investigators,
  errors,
  submitUrl,
  submitMethod,
}: Props) {
  const { data, setData, post, patch, processing, transform } = useForm({
    name: gps_device.name ?? "",
    serial_number: gps_device.serial_number ?? "",
    start_date: gps_device.start_date ?? "",
    end_date: gps_device.end_date ?? "",
    log: gps_device.log ?? "",
    sort_order: gps_device.sort_order,
    notes: gps_device.notes ?? "",
    current_user_id: gps_device.current_user_id,
  })

  transform((data) => ({ gps_device: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitMethod === "post") {
      post(submitUrl)
    } else {
      patch(submitUrl)
    }
  }

  const inputClass =
    "mt-1 block w-full rounded border border-hpi-light-gray px-3 py-2 focus:border-hpi-active-blue focus:outline-none"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Device Information</h3>

          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className={inputClass}
              required
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">{errors.name[0]}</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Serial Number</label>
            <input
              type="text"
              value={data.serial_number}
              onChange={(e) => setData("serial_number", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Sort Order</label>
            <input
              type="number"
              value={data.sort_order ?? ""}
              onChange={(e) =>
                setData(
                  "sort_order",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Assigned To</label>
            <select
              value={data.current_user_id ?? ""}
              onChange={(e) =>
                setData(
                  "current_user_id",
                  e.target.value ? parseInt(e.target.value) : null,
                )
              }
              className={inputClass}
            >
              <option value="">Office (Unassigned)</option>
              {investigators.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            {errors?.current_user_id && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.current_user_id[0]}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium">Dates</h3>

          <div>
            <label className={labelClass}>Start Date</label>
            <input
              type="datetime-local"
              value={data.start_date ?? ""}
              onChange={(e) => setData("start_date", e.target.value)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>End Date</label>
            <input
              type="datetime-local"
              value={data.end_date ?? ""}
              onChange={(e) => setData("end_date", e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div>
        <label className={labelClass}>Log</label>
        <textarea
          value={data.log}
          onChange={(e) => setData("log", e.target.value)}
          rows={3}
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Notes</label>
        <textarea
          value={data.notes}
          onChange={(e) => setData("notes", e.target.value)}
          rows={4}
          className={inputClass}
        />
      </div>

      <div className="flex justify-end space-x-4">
        <a
          href="/gps_devices"
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </a>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {processing ? "Saving..." : "Save GPS Device"}
        </button>
      </div>
    </form>
  )
}
